import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import KEYS from '@/values/keys';
import { required, maxLength, numeric } from 'vuelidate/lib/validators';
import { AUTH_EVENTS, PUBLIC_RATE_LIMIT_WAIT_SECONDS, USER_TYPE, VERIFY_TYPES } from '@/auth/values/auth.values';
import STATES from '@/core/values/states';
import EdRegisterValidatePhoneComponent from '../ed-register-validate-phone/ed-register-validate-phone.component.vue';

@Component({
  name: 'ed-register-basic-info',
  components: {
    EdRegisterValidatePhoneComponent
  }
})
export default class EdRegisterBasicInfoComponent extends Vue {
  
  /* Props */
  @Prop() readonly userType: any;
  @Ref() readonly loading: any;
  /* Data */
  public form: any = {};
  public saving: boolean = false;
  public cities: any = [];
  public countries: any = [];
  public instTypes: any = [];
  public mainCategories: any = [];
  public instCities: any = [];
  public userTypes: any = USER_TYPE;
  public verifyType: any = null;
  public verifyTypes: any = VERIFY_TYPES;

  /* Computed */
  get selectAllOrgCateg () {
    return this.mainCategories.length === this.form.organizationCategoryIdList.value.length
  }

  get selectSomeOrgCateg () {
    return this.form.organizationCategoryIdList.value.length > 0 && !this.selectAllOrgCateg
  }

  get iconOrgCateg () {
    if (this.selectAllOrgCateg) return 'square-xmark-solid'
    if (this.selectSomeOrgCateg) return 'minus-square-solid'
    return 'square'
  }

  get selectAllPersonCateg () {
    return this.mainCategories.length === this.form.personInterestCategoryIdList.value.length
  }

  get selectSomePersonCateg () {
    return this.form.personInterestCategoryIdList.value.length > 0 && !this.selectAllPersonCateg
  }

  get iconPersonCateg () {
    if (this.selectAllPersonCateg) return 'square-xmark-solid'
    if (this.selectSomePersonCateg) return 'minus-square-solid'
    return 'square'
  }

  get required() {
    return [v => !!v || this.$t('errors.required')]
  }

  get requiredElementList() {
    return [
      (list: any) => ((!Array.isArray(list)) || (list?.length > 0)) || this.$t('errors.required'),
    ];
  }

  get canAdvance() {
    return !this.saving
      && this.form.valid
      && (this.userType !== this.userTypes.STUDENT || this.form.personInterestCategoryIdList.value.length > 0)
      && (!this.form.personRepresentOrganization.value || this.form.organizationCategoryIdList.value.length > 0);
  }

  /* Methods */
   private init() {
    this.form = EdFormModel([
      EdInputModel('personFirstName', '', {required, maxLength: maxLength(60)}),
      EdInputModel('personMiddleName', '', {maxLength: maxLength(60)}),
      EdInputModel('personLastName', '', {required, maxLength: maxLength(60)}),
      EdInputModel('personPhoneNumber', '', {required, maxLength: maxLength(15)}),
      EdInputModel('personCountryCode', '', {required}),
      EdInputModel('personCity', '', {required}),
      EdInputModel('personAddress1', '', {required, maxLength: maxLength(200)}),
      EdInputModel('personAddress2', '', {maxLength: maxLength(200)}),
      EdInputModel('personAddress3', '', {maxLength: maxLength(200)}),
      EdInputModel('personZipCode', '', {required}),
      EdInputModel('personState', '', {required}),
      EdInputModel('personPhoneNumberExt', '', {numeric, maxLength: maxLength(7)}, true),
      EdInputModel('personRepresentOrganization', false),
      EdInputModel('personPhoneVerificationCode', ''),
      EdInputModel('personPhoneVerificationId', ''),
      EdInputModel('personInterestCategoryIdList', []),
      EdInputModel('organizationName', '', {maxLength: maxLength(60)}, true),
      EdInputModel('organizationTypeId', ''),
      EdInputModel('organizationRegisteredEnterprise', '', {maxLength: maxLength(60)}, true),
      EdInputModel('organizationEIN', '', {maxLength: maxLength(60)}, true),
      EdInputModel('organizationCategoryIdList', []),
      EdInputModel('organizationWebsite', '', {maxLength: maxLength(100)}, true),
      EdInputModel('organizationPhoneNumber', '', {maxLength: maxLength(15)}, true),
      EdInputModel('organizationPhoneNumberExt', '', {numeric, maxLength: maxLength(7)}, true),
      EdInputModel('organizationSameAddressAsApplicant', false),
      EdInputModel('organizationAddress1', '', {maxLength: maxLength(200)}, true),
      EdInputModel('organizationAddress2', '', {maxLength: maxLength(200)}, true),
      EdInputModel('organizationAddress3', '', {maxLength: maxLength(200)}, true),
      EdInputModel('organizationZipCode', ''),
      EdInputModel('organizationState', ''),
      EdInputModel('organizationCity', ''),
      EdInputModel('organizationCountryCode', '')
    ]);
  }

  next() {
    if (!this.canAdvance) return;
    this.$emit(AUTH_EVENTS.GET_FORM_BASIC_INFO, this.form)
    this.$edEventBus.$emit(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, {step: 3, userType: this.userType })
  }

  goToPrevStep() {
    this.$edEventBus.$emit(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, {step: 1, userType: this.userType})
  }

  async findZipcode(zipcode) {
    try {
      let response = await this.$Auth.Services.validateZipcode(zipcode);
      this.form.set('personState', STATES.find(el => el.state == response.state).name);
      this.cities = response.cities;
    } catch {
      this.form.set('personZipCode', '');
      this.form.set('personState', '');
      this.form.set('personCity', '');
      this.form.setValid('personCity', false);
      this.cities = [];
      this.$EdModal.createMessageModal(
        {
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING,
          body: `${this.$t('auth.register.info.zipcodeNotFound')}`, 
        }
      );
    }
  }

  async findZipcodeInst(zipcode) {
    try {
      let response = await this.$Auth.Services.validateZipcode(zipcode);
      this.form.set('organizationState', STATES.find(el => el.state == response.state).name);
      this.instCities = response.cities;
    } catch {
      this.form.set('organizationZipCode', '');
      this.form.set('organizationState', '');
      this.form.set('organizationCity', '');
      this.form.setValid('organizationCity', false);
      this.instCities = [];
      this.$EdModal.createMessageModal(
        {
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING,
          body: `${this.$t('auth.register.info.zipcodeNotFound')}`, 
        }
      );
    }
  }

  async validateInstitutionName(name) {
    let response = await this.$Auth.Services.validateInstitutionName(name.trim());
    if (response.organizationExists) {
      this.form.set('organizationName', '');
      this.form.setValid('organizationName', false);
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.register'),
        body: this.$i18n.t('auth.register.info.organizationExists'),
        type: KEYS.EVENT_TYPES.WARNING,
      });
    } else this.form.setValid('organizationName', true);
  }

  async validatePhoneModal() {
    if(this.verifyType != VERIFY_TYPES.TEXT_MESSAGE) {
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('auth.register.register'),
          body: this.$i18n.t('auth.register.info.mustSelectVerifyMethod'),
          type: KEYS.EVENT_TYPES.WARNING,
        });
        return
    }
    if(!this.form.personPhoneNumber.value) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.register'),
        body: this.$i18n.t('auth.register.errors.phoneNoValid'),
        type: KEYS.EVENT_TYPES.WARNING,
      });
      return;
    }

    this.showLoading('auth.register.info.validatingPhone');
    let validPhone = await this.findPhone(this.form.personPhoneNumber.value);
    if(validPhone) {        
      let response = await this.sendCode();
      response && this.$EdModal.createModal(EdRegisterValidatePhoneComponent, { 
        phone: this.form.personPhoneNumber.value,
        verificationId: response.verificationId,
        getVerificationResponse: this.getVerificationResponse
      });
    }
    this.hideLoading();
  }

  public getVerificationResponse(verificationId, code) {
    this.form.set('personPhoneVerificationCode', code);
    this.form.setValid('personPhoneVerificationCode', true);
    this.form.set('personPhoneVerificationId', verificationId);
    this.form.setValid('personPhoneVerificationId', true);
  }

  public async sendCode() {    
    try {
      let response = await this.$Auth.Services.sendPhoneCode(this.form.personPhoneNumber.value);
      return response
    } catch (error) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('auth.register.register'),
        body: error?.status === 429
          ? this.$i18n.t('auth.register.errors.phoneCodeRateLimited', { seconds: PUBLIC_RATE_LIMIT_WAIT_SECONDS.SMS_VERIFICATION })
          : this.$i18n.t('auth.register.errors.codeNotSent'),
        type: KEYS.EVENT_TYPES.WARNING
      });
      return false
    }
  }

  setInstAddressValidations (){
    this.form.setValidator('organizationAddress1', 'required', required); 
    this.form.setValidator('organizationZipCode', 'required', required); 
    this.form.setValidator('organizationState', 'required', required); 
    this.form.setValidator('organizationCity', 'required', required); 
    !this.form.organizationCity.value && this.form.setValid('organizationCity', false);
    this.form.setValidator('organizationCountryCode', 'required', required); 
    !this.form.organizationCountryCode.value && this.form.setValid('organizationCountryCode', false);
  }

  removeInstAddressValidations () {
    this.form.removeValidator('organizationAddress1', 'required', required); this.form.setValid('organizationAddress1', true);
    this.form.removeValidator('organizationZipCode', 'required', required); this.form.setValid('organizationZipCode', true);
    this.form.removeValidator('organizationState', 'required', required); this.form.setValid('organizationState', true);
    this.form.removeValidator('organizationCity', 'required', required); this.form.setValid('organizationCity', true);
    this.form.removeValidator('organizationCountryCode', 'required', required); this.form.setValid('organizationCountryCode', true);
  }

  toggleOrgCategories () {
    this.$nextTick(() => {
      if (this.selectAllOrgCateg) {
        this.form.organizationCategoryIdList.value = []
      } else {
        this.form.organizationCategoryIdList.value = this.mainCategories.slice().map(el => el.value)
      }
      this.form.setValid('organizationCategoryIdList', this.form.organizationCategoryIdList.value.length > 0)
    })
  }

  togglePersonCategories () {
    this.$nextTick(() => {
      if (this.selectAllPersonCateg) {
        this.form.personInterestCategoryIdList.value = []
      } else {
        this.form.personInterestCategoryIdList.value = this.mainCategories.slice().map(el => el.value)
      }
      this.form.setValid('personInterestCategoryIdList', this.form.personInterestCategoryIdList.value.length > 0)
    })
  }
  

  async findPhone(phoneNumber) {
    if(phoneNumber != '') {
      try {
        await this.$Auth.Services.existsPhoneNumber(phoneNumber)
        this.$EdModal.createMessageModal(
          {
            body: `${this.$t('auth.register.errors.phoneAlreadyExists')}`, 
            title: `${this.$i18n.t('auth.register.register')}`,
            type: KEYS.EVENT_TYPES.WARNING,
            action: async () => {
              this.form.set('personPhoneNumber', '')
              this.form.setValid('personPhoneNumber', false);  
            }
          });

          return false;
      } catch(error) {
        return true;
      }
    }

    return true;
  }

  public showLoading(label?: string): void {
    try {
        if (this.loading) {
            if (label) {
                const i18Text = this.$i18n.t(label);
                this.loading['show'](i18Text);
            } else {
                this.loading['show']();
            }
        }
    } catch (e) {
        //
    }
  }

  public hideLoading(): void {
    if (this.loading) {
        this.loading['hide']();
    }
  }

  /* Hooks */
  async created() {
    this.init();
    this.countries = await this.$Auth.Services.getCountries();
    this.mainCategories = await this.$Auth.Services.getCategories();
    this.instTypes = await this.$Auth.Services.getInstitutionTypes();
    this.countries = this.countries.map(el => { return { text: el.name, value: el.code } });
    this.mainCategories = this.mainCategories.map(el => { return { text: el.name, value: el.id } });
    this.instTypes = this.instTypes.map(el => { return { text: el.name, value: el.id } })
    this.cities = [];
    this.instCities = [];
    this.form.set('personRepresentOrganization', false);
    this.form.set('organizationSameAddressAsApplicant', false);
    this.verifyType = '';
    if(this.userType == this.userTypes.STUDENT) {
      this.form.setValidator('personInterestCategoryIdList', 'required', required); 
      this.form.setValid('personInterestCategoryIdList', this.form.personInterestCategoryIdList.value.length > 0);
    }
    this.form.setValidator('personPhoneVerificationCode', 'required', required); 
    !this.form.personPhoneVerificationCode.value && this.form.setValid('personPhoneVerificationCode', false);
    this.form.setValidator('personPhoneVerificationId', 'required', required); 
    !this.form.personPhoneVerificationId.value && this.form.setValid('personPhoneVerificationId', false);
  }

  /* Watch */
  @Watch('form.personRepresentOrganization.value')
  institutionFields() {
    if(this.form.personRepresentOrganization.value) {
      this.form.setValidator('organizationName', 'required', required)
      this.form.setValidator('organizationTypeId', 'required', required); 
      !this.form.organizationTypeId.value && this.form.setValid('organizationTypeId', false);
      this.form.setValidator('organizationCategoryIdList', 'required', required); 
      this.form.setValid('organizationCategoryIdList', this.form.organizationCategoryIdList.value.length > 0);
      this.form.setValidator('organizationPhoneNumber', 'required', required);
      this.setInstAddressValidations();
    } else {
      this.form.removeValidator('organizationName', 'required', required); this.form.setValid('organizationName', true);
      this.form.removeValidator('organizationTypeId', 'required', required); this.form.setValid('organizationTypeId', true);
      this.form.removeValidator('organizationCategoryIdList', 'required', required); this.form.setValid('organizationCategoryIdList', true);
      this.form.removeValidator('organizationPhoneNumber', 'required', required); this.form.setValid('organizationPhoneNumber', true);
      if(!this.form.organizationSameAddressAsApplicant.value) {
        this.removeInstAddressValidations();
      }
    } 
  }

  @Watch('form.organizationSameAddressAsApplicant.value')
  institutionAddressFields() {
    if(!this.form.organizationSameAddressAsApplicant.value && this.form.personRepresentOrganization.value) {
      this.setInstAddressValidations();
    } else if (this.form.organizationSameAddressAsApplicant.value && this.form.personRepresentOrganization.value) {
      this.removeInstAddressValidations();
    }
  }
  
}
